import { Slideshow } from 'module.slideshow'
import { EVENTS } from 'util.events'

class ProductImages extends HTMLElement {
  constructor() {
    super()

    this.classes = {
      hidden: 'hide'
    }

    this.selectors = {
      videoParent: '.product__video-wrapper',
      modelParent: '.product__model-wrapper',
      slide: '.product-main-slide',
      currentSlide: '.is-selected',
      startingSlide: '.starting-slide',
      currentVariantJson: '[data-current-variant-json]',
      productOptionsJson: '[data-product-options-json]',
      closeMedia: '.product-single__close-media',
      thumbSlider: '[data-product-thumbs]',
      thumbScroller: '.product__thumbs--scroller',
      mainSlider: '[data-product-photos]',
      imageContainer: '[data-product-images]'
    }

    this.settings = {
      imageSetName: null,
      imageSetIndex: null,
      currentImageSet: null,
      currentSlideIndex: 0,
      mediaGalleryLayout: this.dataset.mediaGalleryLayout,
      hasVideos: this.querySelector(this.selectors.videoParent) ? true : false,
      hasModels: this.querySelector(this.selectors.modelParent) ? true : false
    }
  }

  // Pause all playing media elements except the provided one (if any)
  pauseAllMediaExcept(current) {
    this.querySelectorAll('video-media[playing], model-media[playing]').forEach((el) => {
      if (!current || el !== current) {
        el.pause()
      }
    })
  }

  connectedCallback() {
    if (!this.dataset.modal) return

    this.abortController = new AbortController()
    this.videoObjects = {}

    this.sectionId = this.getAttribute('data-section-id')

    this.currentVariant = JSON.parse(this.querySelector(this.selectors.currentVariantJson).textContent)
    this.productOptions = JSON.parse(this.querySelector(this.selectors.productOptionsJson).textContent)

    this.cacheElements()
    this.updateSetPictureThumb()

    const dataSetEl = this.cache.mainSlider.querySelector('[data-set-name]')
    if (dataSetEl) {
      this.settings.imageSetName = dataSetEl.dataset.setName
      this.settings.imageSetIndex =
        'option' + (this.productOptions.findIndex((opt) => this.getImageSetName(opt) == this.settings.imageSetName) + 1)
    }

    this.initVariants()
    this.initImageZoom()
    this.initProductSlider(this.currentVariant)
    this.customMediaListeners()
    this.initLeashColourGallery()
    this.initB2CCollarSupplement()

    if (this.hasAttribute('data-astra-vertical-gallery')) {
      matchMedia('(max-width: 768px)').addEventListener('change', () => {
        if (!this.flickity) return
        this.flickity.args.childVertical = !matchMedia('(max-width: 768px)').matches
        this.flickity.childNavGoTo(this.settings.currentSlideIndex)
      }, { signal: this.abortController.signal })
    }
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.galleryObserver?.disconnect()
    this.galleryWarmupController?.abort()

    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy()
    }
  }

  cacheElements() {
    this.cache = {
      mainSlider: this.querySelector(this.selectors.mainSlider),
      thumbSlider: this.querySelector(this.selectors.thumbSlider),
      thumbScroller: this.querySelector(this.selectors.thumbScroller)
    }
  }

  initVariants() {
    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.updateVariantImage.bind(this),
      { signal: this.abortController.signal }
    )

    // image set names variant change listeners
    if (this.settings.imageSetIndex) {
      document.addEventListener(
        `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
        this.updateImageSet.bind(this),
        { signal: this.abortController.signal }
      )
    }
  }

  // A real, draft-only BBO frontal photo follows the native colour choice.
  // It is added after that colour's original hero; it never changes a variant.
  initB2CCollarSupplement() {
    this.b2cSupplementTemplate = this.querySelector('template[data-b2c-collar-supplement]')
    if (!this.b2cSupplementTemplate) return
    this.syncB2CCollarSupplement(this.currentVariant)
  }

  refreshB2CPhotoIndices() {
    this.cache.mainSlider.querySelectorAll(this.selectors.slide).forEach((slide, index) => {
      slide.dataset.index = String(index)
      slide.querySelectorAll('img[data-index]').forEach((image) => { image.dataset.index = String(index + 1) })
    })
    this.cache.thumbScroller?.querySelectorAll('.product__thumb-item').forEach((thumb, index) => {
      thumb.dataset.index = String(index)
      thumb.querySelector('[data-product-thumb]').dataset.index = String(index)
    })
    if (this.flickity && this.cache.thumbSlider) {
      this.flickity.childNavEls = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide)')
      this.flickity.childNavLinks = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide) a')
    }
  }

  syncB2CCollarSupplement(variant) {
    if (!this.b2cSupplementTemplate || !variant) return
    if (this.hasAttribute('data-astra-leash-colour-gallery') && !this.leashColourChosen) return
    const matches = variant.sku?.startsWith('COLBBO001')
    if (this.b2cSupplementSlide) {
      if (matches) return
      // Leave an outgoing supplement before removing it, including from zoom.
      if (this.flickity) this.flickity.goToSlide(0)
      this.b2cSupplementThumb.remove()
      if (this.flickity) {
        this.flickity.childNavEls = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide)')
        this.flickity.childNavLinks = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide) a')
        this.flickity.slideshow.remove(this.b2cSupplementSlide)
      } else this.b2cSupplementSlide.remove()
      this.b2cSupplementSlide = null
      this.b2cSupplementThumb = null
      this.refreshB2CPhotoIndices()
      return
    }
    if (!matches) return
    const original = this.cache.mainSlider.querySelector(`.product-main-slide[data-media-id="${variant.featured_media?.id || variant.image_id}"]`)
    if (!original) return
    const index = Number(original.dataset.index) + 1
    const slide = this.b2cSupplementTemplate.content.querySelector('.product-main-slide').cloneNode(true)
    const thumb = this.b2cSupplementTemplate.content.querySelector('.product__thumb-item').cloneNode(true)
    slide.dataset.index = String(index)
    slide.querySelector('img[data-index]').dataset.index = String(index + 1)
    thumb.dataset.index = String(index)
    const link = thumb.querySelector('[data-product-thumb]')
    link.dataset.index = String(index)
    const select = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter') return
      event.preventDefault()
      this.loadGalleryImage(slide)
      if (this.flickity) this.flickity.goToSlide(Number(link.dataset.index))
      else this.scrollToStackedMedia(Number(link.dataset.index))
    }
    for (const type of ['click', 'focus', 'keydown']) link.addEventListener(type, select, { signal: this.abortController.signal })
    // PhotoSwipe binds existing buttons on first use. A later reinserted image
    // gets its own binding so zoom works after leaving and returning to BBO.
    if (this.photoswipe) slide.querySelector('.js-photoswipe__zoom')?.addEventListener('click', (event) => {
      event.preventDefault()
      this.photoswipe.triggerClick({ currentTarget: event.currentTarget, astraSlide: slide })
    }, { signal: this.abortController.signal })
    this.cache.thumbScroller.insertBefore(thumb, this.cache.thumbScroller.children[index] || null)
    if (this.flickity) {
      this.flickity.childNavEls = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide)')
      this.flickity.childNavLinks = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide) a')
      this.flickity.slideshow.insert(slide, index)
    } else {
      const track = this.cache.mainSlider.querySelector('[data-astra-gallery-slider]') || this.cache.mainSlider
      track.insertBefore(slide, original.nextSibling)
    }
    this.b2cSupplementSlide = slide
    this.b2cSupplementThumb = thumb
    this.refreshB2CPhotoIndices()
    this.loadGalleryImage(slide)
    this.flickity?.resize()
  }

  // Collar and leash packshots stay inert until an explicit colour choice.
  initLeashColourGallery() {
    if (!this.hasAttribute('data-astra-leash-colour-gallery')) return
    this.leashVariants = JSON.parse(this.querySelector('[data-astra-leash-variants-json]')?.textContent || '[]')
    this.leashColourChosen = false
    const chooseColour = (event) => {
      const input = event.target
      if (!(input instanceof HTMLElement) || !input.matches('input[type="radio"], select')) return
      if (event.type === 'click' && input.tagName !== 'INPUT') return
      const picker = input.closest('kit-variant-picker')
      if (picker?.dataset.productId !== this.dataset.productId || picker?.dataset.sectionId !== this.sectionId) return
      const optionName = input.tagName === 'SELECT' ? input.dataset.optionName : input.name
      if (optionName !== this.productOptions[0]) return
      this.leashColourChosen = true
      this.updateLeashColourMedia(picker)
    }
    document.addEventListener('click', chooseColour, { signal: this.abortController.signal })
    document.addEventListener('change', chooseColour, { signal: this.abortController.signal })
  }

  updateLeashColourMedia(picker) {
    if (!this.leashColourChosen) return
    picker ||= document.querySelector(`kit-variant-picker[data-product-id="${this.dataset.productId}"][data-section-id="${this.sectionId}"]`)
    if (!picker) return
    const selected = [...picker.querySelectorAll('input[type="radio"]:checked, select')].map((input) => input.value)
    // A sold-out/unavailable combination can lack a resolved variant. Keep the
    // colour the visitor chose, using its other size's photo if necessary.
    const variant = this.leashVariants.find((item) => item.options.every((value, index) => value === selected[index])) ||
      this.leashVariants.find((item) => item.options[0] === selected[0])
    const mediaId = variant?.mediaId == null ? null : String(variant.mediaId)
    const template = mediaId && this.querySelector(`template[data-astra-leash-packshot="${mediaId}"]`)
    if (!template) return

    // Remove an outgoing BBO supplement before changing the colour photo.
    if (this.leashCurrentMediaId !== mediaId && this.b2cSupplementSlide) this.syncB2CCollarSupplement({ sku: '' })
    if (this.leashCurrentMediaId !== mediaId) {
      const slide = template.content.querySelector('.product-main-slide')?.cloneNode(true)
      const thumb = template.content.querySelector('.product__thumb-item')?.cloneNode(true)
      if (!slide || !thumb) return
      this.galleryWarmupController?.abort()
      // Move off an outgoing colour before removing its slide and thumbnail.
      if (this.leashColourSlide && this.flickity) this.flickity.goToSlide(0)
      if (this.leashColourSlide) {
        if (this.flickity) this.flickity.slideshow.remove(this.leashColourSlide)
        else this.leashColourSlide.remove()
        this.leashColourThumb?.remove()
      }
      const index = this.cache.mainSlider.querySelectorAll(this.selectors.slide).length
      slide.dataset.index = String(index)
      slide.querySelectorAll('[data-index]').forEach((node) => { node.dataset.index = String(index + 1) })
      thumb.dataset.index = String(index)
      const link = thumb.querySelector('[data-product-thumb]')
      link.dataset.index = String(index)
      const selectPhoto = (event) => {
        if (event.type === 'keydown' && event.key !== 'Enter') return
        event.preventDefault()
        if (this.flickity) this.flickity.goToSlide(Number(link.dataset.index))
        else this.scrollToStackedMedia(Number(link.dataset.index))
      }
      link.addEventListener('click', selectPhoto, { signal: this.abortController.signal })
      link.addEventListener('focus', selectPhoto, { signal: this.abortController.signal })
      link.addEventListener('keydown', selectPhoto, { signal: this.abortController.signal })
      this.cache.thumbScroller?.append(thumb)
      if (this.flickity) {
        // Refresh navigation before Flickity emits callbacks for the new cell.
        if (this.cache.thumbSlider) {
          this.flickity.childNavEls = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide)')
          this.flickity.childNavLinks = this.cache.thumbSlider.querySelectorAll('.product__thumb-item:not(.hide) a')
        }
        this.flickity.slideshow.append(slide)
      } else {
        const track = this.cache.mainSlider.querySelector('[data-astra-gallery-slider]') || this.cache.mainSlider
        track.append(slide)
      }
      this.leashColourSlide = slide
      this.leashColourThumb = thumb
      this.leashCurrentMediaId = mediaId
      if (this.photoswipe) slide.querySelector('.js-photoswipe__zoom')?.addEventListener('click', (event) => {
        event.preventDefault()
        this.photoswipe.triggerClick({ currentTarget: event.currentTarget, astraSlide: slide })
      }, { signal: this.abortController.signal })
      this.refreshB2CPhotoIndices()
    }
    this.syncB2CCollarSupplement({ sku: variant.sku, featured_media: { id: mediaId } })
    this.loadGalleryImage(this.leashColourSlide)
    const index = Number(this.leashColourSlide.dataset.index)
    if (this.flickity) this.flickity.goToSlide(index)
    else {
      this.scrollToStackedMedia(index)
      this.handleStackedMediaChange(index)
    }
  }

  /*============================================================================
    Variant change methods
  ==============================================================================*/
  imageSetArguments(variant) {
    variant = variant ? variant : this.variants ? this.variants.currentVariant : null
    if (!variant) return

    const setValue = (this.settings.currentImageSet = this.getImageSetName(variant[this.settings.imageSetIndex]))
    const set = `${this.settings.imageSetName}_${setValue}`

    // Always start on index 0
    this.settings.currentSlideIndex = 0

    // Return object that adds cellSelector to mainSliderArgs
    return {
      cellSelector: '[data-group="' + set + '"]',
      imageSet: set,
      initialIndex: this.settings.currentSlideIndex
    }
  }

  updateImageSet(evt) {
    // If called directly, use current variant
    const variant = evt ? evt.detail.variant : this.variants ? this.variants.currentVariant : null
    if (!variant) {
      return
    }

    const setValue = this.getImageSetName(variant[this.settings.imageSetIndex])

    // Already on the current image group
    if (this.settings.currentImageSet === setValue) {
      return
    }

    this.initProductSlider(variant)
    /**
     * @event product-images:updateImageSet
     * @description Triggered when the image set is updated.
     */
    this.dispatchEvent(new CustomEvent('product-images:updateImageSet', { bubbles: true }))
  }

  // Show/hide thumbnails based on current image set
  updateImageSetThumbs(set) {
    this.cache.thumbSlider?.querySelectorAll('.product__thumb-item').forEach((thumb) => {
      thumb.classList.toggle(this.classes.hidden, thumb.dataset.group !== set)
    })
  }

  getImageSetName(string) {
    return string
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '')
  }

  /*============================================================================
    Product images
  ==============================================================================*/
  initImageZoom() {
    const container = this
    // Keep the first mouse, touch or keyboard click while the optional zoom
    // library loads. The main gallery and variant controls do not wait for it.
    container.addEventListener('click', async (event) => {
      const trigger = event.target.closest('.js-photoswipe__zoom')
      if (!trigger || !container.contains(trigger) || this.photoswipe || container.dataset.zoom === 'false') return
      event.preventDefault()
      if (this.zoomLoading) return
      this.zoomLoading = true
      const slide = trigger.closest('.product-main-slide')
      trigger.setAttribute('aria-busy', 'true')
      this.querySelector('[data-astra-zoom-fallback]')?.remove()
      try {
        const { default: Photoswipe } = await import('product.gallery.photoswipe')
        if (!this.isConnected || !trigger.isConnected) return
        this.photoswipe = new Photoswipe(this, this.sectionId)
        this.photoswipe.triggerClick({ currentTarget: trigger, astraSlide: slide })
      } catch (_) {
        // A failed optional download must not hide the full-size photograph.
        const image = slide?.querySelector('.photoswipe__image')
        const source = image?.getAttribute('data-photoswipe-src')
        if (source && trigger.isConnected) {
          const link = document.createElement('a')
          link.dataset.astraZoomFallback = ''
          link.href = source
          link.target = '_blank'
          link.rel = 'noopener'
          link.textContent = document.documentElement.lang.startsWith('fr') ? 'Ouvrir l’image en grand' : 'Open full-size image'
          trigger.after(link)
        }
      } finally {
        trigger.removeAttribute('aria-busy')
        this.zoomLoading = false
      }
    }, { signal: this.abortController.signal })
    container.addEventListener(
      'photoswipe:afterChange',
      function (evt) {
        if (this.flickity) {
          this.flickity.goToSlide(evt.detail.index)
        }
      }.bind(this)
    )
    // Execute JS modules after the tooltip is opened
    document.addEventListener('tooltip:open', (e) => {
      if (e.detail?.context !== 'QuickShop') return
      const scripts = document.querySelectorAll('tool-tip product-component script[type="module"]')
      for (let i = 0; i < scripts.length; i++) {
        let script = document.createElement('script')
        script.type = 'module'
        script.textContent = scripts[i].textContent
        scripts[i].parentNode.replaceChild(script, scripts[i])
      }
    }, { signal: this.abortController.signal })
  }

  getThumbIndex(target) {
    return target.dataset.index
  }

  updateVariantImage(evt) {
    const variant = evt?.detail?.variant

    // Ignore events for different product ids to prevent background scroll
    const originProductRoot = evt?.target?.closest('[data-product-id]')
    if (originProductRoot && originProductRoot.getAttribute('data-product-id') !== this.dataset.productId) {
      return
    }

    if (this.hasAttribute('data-astra-leash-colour-gallery')) {
      if (this.hasAttribute('data-b2c-bed-overview-media-id') && variant) {
        const sizeOnly = this.currentVariant?.options?.[0] === variant.options?.[0] && this.currentVariant?.options?.at(-1) !== variant.options?.at(-1)
        this.currentVariant = variant
        if (sizeOnly) { this.flickity?.goToSlide(0); return }
      }
      this.updateLeashColourMedia()
      return
    }
    if (!variant) return
    this.syncB2CCollarSupplement(variant)
    const setPicture = this.querySelector('[data-astra-set-picture]')
    const nextSetPicture = evt?.detail?.html?.querySelector(`product-images[data-product-id="${this.dataset.productId}"] [data-astra-set-picture]`)
    if (setPicture && nextSetPicture) {
      setPicture.innerHTML = nextSetPicture.innerHTML
      this.updateSetPictureThumb()
      this.flickity?.resize()
    }
    // A variant without its own photo must not keep showing the previous colour.
    // Changing bed size returns to the range photo; colour choices keep native media.
    const previousBedVariant = this.currentVariant
    const bedSizeChanged = this.hasAttribute('data-b2c-bed-overview-media-id') &&
      previousBedVariant?.options?.[0] === variant.options?.[0] &&
      previousBedVariant?.options?.at(-1) !== variant.options?.at(-1)
    if (this.hasAttribute('data-b2c-bed-overview-media-id')) this.currentVariant = variant
    const matLifestyleMap = this.querySelector('[data-b2c-mat-lifestyle-map]')
    let matLifestyleId
    if (matLifestyleMap) {
      try {
        matLifestyleId = JSON.parse(matLifestyleMap.textContent)[variant.id]
      } catch {
        // Missing/invalid mappings keep Shopify's native variant photo fallback.
      }
    }
    const mediaId = bedSizeChanged
      ? this.dataset.b2cBedOverviewMediaId
      : matLifestyleId || variant.featured_media?.id || this.dataset.defaultMediaId
    if (!mediaId) return
    if (!matchMedia('(max-width: 768px)').matches && this.settings.mediaGalleryLayout === 'stacked') {
      const slide = this.cache.mainSlider.querySelector(
        `.product-main-slide[data-media-id="${mediaId}"]`
      )

      if (!slide) return
      const imageIndex = this.getThumbIndex(slide)

      this.loadGalleryImage(slide)
      this.scrollToStackedMedia(imageIndex)

      this.handleStackedMediaChange(imageIndex)
    } else {
      const newImage = this.querySelector(`.product-main-slide[data-media-id="${mediaId}"]`)
      if (!newImage) return
      this.loadGalleryImage(newImage)
      const imageIndex = this.getThumbIndex(newImage)

      // If there is no index, slider is not initalized
      if (typeof imageIndex === 'undefined') {
        return
      }

      // Go to that variant image's slide
      if (this.flickity) {
        this.flickity.goToSlide(imageIndex)
      }
    }
  }

  // Keep the component-photo fallback thumbnail in sync after theme-only media reordering.
  updateSetPictureThumb() {
    const picture = this.cache.mainSlider?.querySelector('[data-astra-set-picture]')
    if (!picture) return
    const mediaId = picture.closest('.product-main-slide')?.dataset.mediaId
    if (!mediaId) return
    const thumb = this.cache.thumbSlider?.querySelector(`[data-product-thumb][data-id="${mediaId}"]`)
    const wrap = thumb?.querySelector('.image-wrap__thumbnail')
    if (!wrap) return

    if (!this.astraSetThumbOriginal) {
      this.astraSetThumbOriginal = {
        href: thumb.getAttribute('href'),
        label: thumb.getAttribute('aria-label')
      }
    }
    wrap.querySelector('[data-astra-set-thumb]')?.remove()
    const images = picture.querySelectorAll('.astra-set-picture__items img')
    if (images.length !== 2) {
      for (const [attribute, value] of Object.entries({
        href: this.astraSetThumbOriginal.href,
        'aria-label': this.astraSetThumbOriginal.label
      })) {
        if (value === null) thumb.removeAttribute(attribute)
        else thumb.setAttribute(attribute, value)
      }
      return
    }

    const preview = document.createElement('span')
    preview.dataset.astraSetThumb = ''
    preview.setAttribute('aria-hidden', 'true')
    images.forEach((image) => {
      const miniature = image.cloneNode(false)
      miniature.removeAttribute('id')
      miniature.alt = ''
      miniature.sizes = '40px'
      miniature.loading = 'lazy'
      if (miniature.dataset.gallerySrcset) miniature.srcset = miniature.dataset.gallerySrcset
      if (miniature.dataset.gallerySrc) miniature.src = miniature.dataset.gallerySrc
      delete miniature.dataset.gallerySrc
      delete miniature.dataset.gallerySrcset
      preview.append(miniature)
    })
    wrap.append(preview)
    if (!picture.id) picture.id = `AstraSetPicture-${this.sectionId}-${this.dataset.productId}`
    thumb.setAttribute('href', `#${picture.id}`)
    const caption = picture.querySelector('figcaption')
    if (caption) {
      const label = [...caption.childNodes].map((node) => node.textContent).join(' ')
      thumb.setAttribute('aria-label', label.replace(/\s+/g, ' ').trim())
    }
  }

  stackedMediaInit() {
    const mediaGalleryElements = this.querySelectorAll('.product-slideshow .product-main-slide')

    this.mediaObservers = []

    for (let index = 0; index < mediaGalleryElements.length; index++) {
      const slideElement = mediaGalleryElements[index]
      const mediaObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              this.settings.currentSlideIndex = index
              this.handleStackedMediaChange(this.settings.currentSlideIndex)
            }
          })
        },
        {
          root: null, // Use the viewport as the root
          rootMargin: '400px 0px 0px 0px', // Adjust the top offset to delay the intersection
          threshold: [0, 0.5, 1]
        }
      )

      mediaObserver.observe(slideElement)

      // Store mediaObserver instance in array
      this.mediaObservers.push(mediaObserver)
    }
  }

  handleStackedMediaChange(index) {
    const mediaTarget = this.querySelectorAll('.product-slideshow .product-main-slide')[index]

    if (!mediaTarget) return

    if (this.settings.hasVideos) {
      const video = mediaTarget.querySelector(this.selectors.videoParent)
      if (video) {
        const isLandEl = video.querySelector('is-land')
        const videoMediaEl = video.querySelector('video-media')
        isLandEl.dispatchEvent(new CustomEvent('select'))
        if (
          videoMediaEl.hasAttribute('autoplay') &&
          !videoMediaEl.hasAttribute('playing') &&
          videoMediaEl.hasAttribute('loaded')
        ) {
          this.pauseAllMediaExcept(videoMediaEl)
          videoMediaEl.play()
        }
      }
    }

    if (this.settings.hasModels) {
      const model = mediaTarget.querySelector(this.selectors.modelParent)
      if (model) {
        const isLandEl = model.querySelector('is-land')
        const modelMediaEl = model.querySelector('model-media')
        isLandEl.dispatchEvent(new CustomEvent('select'))
        if (
          modelMediaEl.hasAttribute('autoplay') &&
          !modelMediaEl.hasAttribute('playing') &&
          modelMediaEl.hasAttribute('loaded')
        ) {
          this.pauseAllMediaExcept(modelMediaEl)
          modelMediaEl.play()
        }
      }

      const currentMedia = mediaTarget.querySelector(this.selectors.media)
      if (currentMedia) {
        currentMedia.dispatchEvent(
          /**
           * @event mediaVisible
           * @description Event fired when media is visible.
           * @param {object} detail - The detail object.
           * @param {boolean} detail.autoplayMedia - Whether the media should autoplay or not.
           * @param {boolean} bubbles - Whether the event bubbles up through the DOM or not.
           * @param {boolean} cancelable - Whether the event is cancelable or not.
           */
          new CustomEvent('mediaVisible', {
            bubbles: true,
            cancelable: true,
            detail: {
              autoplayMedia: false
            }
          })
        )
        mediaTarget.querySelector('.shopify-model-viewer-ui__button').setAttribute('tabindex', 0)
        mediaTarget.querySelector('.product-single__close-media').setAttribute('tabindex', 0)
      }
    }
  }

  scrollToStackedMedia(index) {
    const mediaTarget = this.querySelectorAll('.product-slideshow .product-main-slide')[index]

    if (!mediaTarget) return

    const scroller = this.closest('[data-tool-tip-content]') || document.querySelector('tool-tip[data-tool-tip-open="true"] [data-tool-tip-content]')
    const isWindow = !scroller

    const top = isWindow
      ? mediaTarget.getBoundingClientRect().top + window.pageYOffset
      : mediaTarget.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop

    const elementToScroll = isWindow ? window : scroller

    elementToScroll.scrollTo({
      top,
      behavior: 'smooth'
    })
  }

  // Activate the selected image immediately; optional neighbours wait until it
  // has loaded, then until idle time. Do not fetch the last slide on first load.
  loadGalleryImage(slide) {
    const hasSetPicture = slide?.querySelectorAll('[data-astra-set-picture] .astra-set-picture__items img').length === 2
    slide?.querySelectorAll('img[data-gallery-src]').forEach((img) => {
      // A valid two-piece Walk Set photo covers this slide's native fallback.
      // Keep the native source deferred; a later variant without that custom
      // picture will activate it through the same function.
      if (hasSetPicture && !img.closest('[data-astra-set-picture]')) return
      img.loading = 'eager'
      if (img.dataset.gallerySrcset) img.srcset = img.dataset.gallerySrcset
      img.src = img.dataset.gallerySrc
      delete img.dataset.gallerySrc
      delete img.dataset.gallerySrcset
    })
    slide?.querySelectorAll('video[data-gallery-poster]').forEach((video) => {
      video.poster = video.dataset.galleryPoster
      delete video.dataset.galleryPoster
    })
  }

  loadGalleryNeighbours(slide, initial = false) {
    if (!slide) return
    this.galleryWarmupController?.abort()
    const controller = new AbortController()
    this.galleryWarmupController = controller
    const slides = [...this.cache.mainSlider.querySelectorAll(this.selectors.slide)]
    const index = slides.indexOf(slide)
    this.loadGalleryImage(slide)
    // B2C starts with the visible hero only. Warm neighbours after gallery use,
    // rather than downloading a hidden full-size photograph during page load.
    if (initial && this.closest('.b2c-product')) return
    if (index < 0) return
    const warm = () => {
      if (controller.signal.aborted || !this.isConnected) return
      // The next photograph is useful after the hero; wrapping backwards to
      // an unrelated final variant image is useful only after gallery use.
      this.loadGalleryImage(slides[index + 1] || (!initial ? slides[0] : null))
      if (!initial) this.loadGalleryImage(slides[(index - 1 + slides.length) % slides.length])
    }
    const idle = () => {
      if (controller.signal.aborted) return
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(warm, { timeout: 1800 })
        controller.signal.addEventListener('abort', () => window.cancelIdleCallback(id), { once: true })
      } else {
        const id = window.setTimeout(warm, 350)
        controller.signal.addEventListener('abort', () => window.clearTimeout(id), { once: true })
      }
    }
    const image = slide.querySelector('img')
    if (!image || (image.complete && image.naturalWidth > 0)) idle()
    else {
      image.addEventListener('load', idle, { once: true, signal: controller.signal })
      image.addEventListener('error', idle, { once: true, signal: controller.signal })
    }
  }

  initProductSlider(variant) {
    // Stop if only a single image, but add active class to first slide
    if (this.cache.mainSlider.querySelectorAll(this.selectors.slide).length <= 1) {
      const slide = this.cache.mainSlider.querySelector(this.selectors.slide)
      if (slide) {
        slide.classList.add('is-selected')
      }
      return
    }

    // Destroy slider in preparation of new initialization
    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy()
    }

    // If variant argument exists, slideshow is reinitializing because of the
    // image set feature enabled and switching to a new group.
    // currentSlideIndex
    if (variant) {
      const activeSlide = this.cache.mainSlider.querySelector(this.selectors.startingSlide)
      this.settings.currentSlideIndex = this._slideIndex(activeSlide)
    }

    let mainSliderArgs = {
      dragThreshold: 25,
      adaptiveHeight: true,
      avoidReflow: true,
      initialIndex: this.settings.currentSlideIndex,
      childNav: this.cache.thumbSlider,
      childNavScroller: this.cache.thumbScroller,
      childVertical: this.cache.thumbSlider?.dataset.position === 'beside' &&
        (!this.hasAttribute('data-astra-vertical-gallery') || !matchMedia('(max-width: 768px)').matches),
      pageDots: true, // mobile only with CSS
      wrapAround: true,
      // B2C pages: left-aligned cells so the mobile peek shows the next photo only (full-width cells look the same).
      cellAlign: this.closest('.b2c-product') ? 'left' : 'center',
      callbacks: {
        onInit: this.onSliderInit.bind(this),
        onChange: this.onSlideChange.bind(this)
      }
    }

    // Override default settings if image set feature enabled
    if (this.settings.imageSetName) {
      const imageSetArgs = this.imageSetArguments(variant)
      mainSliderArgs = Object.assign({}, mainSliderArgs, imageSetArgs)
      this.updateImageSetThumbs(mainSliderArgs.imageSet)
    }

    if (!matchMedia('(max-width: 768px)').matches && this.settings.mediaGalleryLayout === 'stacked') {
      const imageContainer = this.querySelector(this.selectors.imageContainer) || this
      imageContainer.setAttribute('data-has-slideshow', 'false')

      this.galleryObserver?.disconnect()
      this.galleryObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) this.loadGalleryImage(target)
        })
      }, { rootMargin: '300px' })
      this.cache.mainSlider.querySelectorAll(this.selectors.slide).forEach((slide) => this.galleryObserver.observe(slide))
      this.stackedMediaInit()

      if (variant && variant.featured_media && this.cache.mainSlider && !this.hasAttribute('data-astra-lifestyle-opener')) {
        const slide = this.cache.mainSlider.querySelector(
          `.product-main-slide[data-media-id="${variant.featured_media.id}"]`
        )
        if (slide) {
          const imageIndex = parseInt(this.getThumbIndex(slide), 10)
          if (!Number.isNaN(imageIndex)) {
            requestAnimationFrame(() => {
              this.scrollToStackedMedia(imageIndex)
              this.handleStackedMediaChange(imageIndex)
            })
          }
        }
      }

      return
    }

    this.flickity = new Slideshow(this.cache.mainSlider, mainSliderArgs)

    // Ensure we resize the slider to avoid reflow issues
    setTimeout(() => {
      this.flickity.resize()
    }, 100)
  }

  onSliderInit(slide) {
    this.loadGalleryNeighbours(slide, true)
    const counter = this.querySelector('[data-astra-gallery-current]')
    if (counter) counter.textContent = String(Number(slide.dataset.index || 0) + 1)
    // If slider is initialized with image set feature active,
    // initialize any videos/media when they are first slide
    const video = slide.querySelector(this.selectors.videoParent)
    if (video) {
      const isLandEl = video.querySelector('is-land')
      isLandEl.dispatchEvent(new CustomEvent('select'))
    }

    const model = slide.querySelector(this.selectors.modelParent)
    if (model) {
      const isLandEl = model.querySelector('is-land')
      isLandEl.dispatchEvent(new CustomEvent('select'))
    }

    if (this.settings.imageSetName) {
      this.prepMediaOnSlide(slide, true)
    }
  }

  onSlideChange(index) {
    const counter = this.querySelector('[data-astra-gallery-current]')
    if (counter) counter.textContent = String(index + 1)
    if (!this.flickity) return

    const prevSlide = this.cache.mainSlider.querySelector(
      '.product-main-slide[data-index="' + this.settings.currentSlideIndex + '"]'
    )

    // If imageSetName exists, use a more specific selector
    const nextSlide = this.settings.imageSetName
      ? this.cache.mainSlider.querySelectorAll('.flickity-slider .product-main-slide')[index]
      : this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + index + '"]')

    prevSlide.setAttribute('tabindex', '-1')
    nextSlide.setAttribute('tabindex', 0)

    // Pause any existing slide video/media
    this.stopMediaOnSlide(prevSlide)

    // Prep next slide video/media
    this.prepMediaOnSlide(nextSlide)

    // Update current slider index
    this.settings.currentSlideIndex = index
  }

  stopMediaOnSlide(slide) {
    // Stop existing video
    const video = slide.querySelector(this.selectors.videoParent)
    if (video) {
      const videoMediaEl = video.querySelector('video-media')
      if (videoMediaEl.hasAttribute('playing')) {
        videoMediaEl.pause()
      }
    }

    // Stop existing media
    const currentMedia = slide.querySelector(this.selectors.modelParent)
    if (currentMedia) {
      const modelMediaEl = currentMedia.querySelector('model-media')
      if (modelMediaEl.hasAttribute('playing')) {
        modelMediaEl.pause()
      }
    }
  }

  prepMediaOnSlide(slide, initial = false) {
    this.loadGalleryNeighbours(slide, initial)
    const video = slide.querySelector(this.selectors.videoParent)
    if (video) {
      this.flickity.reposition()
      const isLandEl = video.querySelector('is-land')
      const videoMediaEl = video.querySelector('video-media')
      isLandEl.dispatchEvent(new CustomEvent('select'))
      if (
        videoMediaEl.hasAttribute('autoplay') &&
        !videoMediaEl.hasAttribute('playing') &&
        videoMediaEl.hasAttribute('loaded')
      ) {
        videoMediaEl.play()
      }
    }

    const nextMedia = slide.querySelector(this.selectors.modelParent)
    if (nextMedia) {
      this.flickity.reposition()
      const isLandEl = nextMedia.querySelector('is-land')
      const modelMediaEl = nextMedia.querySelector('model-media')
      isLandEl.dispatchEvent(new CustomEvent('select'))
      if (
        modelMediaEl.hasAttribute('autoplay') &&
        !modelMediaEl.hasAttribute('playing') &&
        modelMediaEl.hasAttribute('loaded')
      ) {
        modelMediaEl.play()
      }
    }
  }

  _slideIndex(el) {
    return el.getAttribute('data-index')
  }

  customMediaListeners() {
    document.querySelectorAll(this.selectors.closeMedia).forEach((el) => {
      el.addEventListener(
        'click',
        function () {
          let slide

          if (this.settings.mediaGalleryLayout === 'stacked') {
            slide = this.cache.mainSlider.querySelector(
              `.product-main-slide[data-index="${this.settings.currentSlideIndex}"]`
            )
          } else {
            slide = this.cache.mainSlider.querySelector(this.selectors.currentSlide)
          }

          const media = slide.querySelector(this.selectors.media)
          if (media) {
            media.dispatchEvent(
              new CustomEvent('mediaHidden', {
                bubbles: true,
                cancelable: true
              })
            )
          }
        }.bind(this)
      )
    })

    const modelViewers = this.querySelectorAll('model-viewer')
    if (modelViewers.length) {
      modelViewers.forEach((el) => {
        el.addEventListener('shopify_model_viewer_ui_toggle_play', this.mediaLoaded.bind(this), {
          signal: this.abortController.signal
        })

        el.addEventListener('shopify_model_viewer_ui_toggle_pause', this.mediaUnloaded.bind(this), {
          signal: this.abortController.signal
        })
      })
    }

    // Enforce single playback: when any media starts playing, pause others
    this.addEventListener(
      'media:play',
      (evt) => {
        const current = evt.target
        this.querySelectorAll('video-media[playing], model-media[playing]').forEach((el) => {
          if (el !== current) {
            el.pause()
          }
        })
      },
      { signal: this.abortController.signal }
    )
  }

  mediaLoaded(evt) {
    if (this.flickity) {
      this.flickity.setDraggable(false)
    }
  }

  mediaUnloaded(evt) {
    if (this.flickity) {
      this.flickity.setDraggable(true)
    }
  }
}

customElements.define('product-images', ProductImages)
